import { type ReactElement } from 'react';

import type { RadioGroupProps } from './types';

import { typedMemo } from '../../utils/typedMemo';
import { Layout } from '../Layout/Layout';
import { Radio } from './Radio/Radio';
import { optionInputValue } from './utils';
import './RadioGroup.css';

function RadioGroupComponent<V = string>({
  label,
  name,
  options,
  value,
  onChange,
  disabled,
  required,
  status,
  __private,
}: RadioGroupProps<V>) {
  const { ariaDescribedBy, ariaLabelledBy, id } = __private ?? {};
  const accessibleName = ariaLabelledBy ? undefined : label;

  return (
    <Layout
      aria-describedby={ariaDescribedBy}
      aria-invalid={status === 'critical' || undefined}
      aria-label={accessibleName}
      aria-labelledby={ariaLabelledBy}
      aria-required={required || undefined}
      className="eds-radio-group"
      direction="column"
      id={id}
      role="radiogroup"
      spacing="s"
    >
      {options.map((option, index) => (
        <Radio
          key={optionInputValue(option.value, index)}
          disabled={disabled || option.disabled}
          hint={option.hint}
          label={option.label ?? option.text}
          name={name}
          selected={Object.is(value, option.value)}
          text={option.text}
          value={optionInputValue(option.value, index)}
          onSelectedChange={(selected) => {
            if (selected) {
              onChange(option.value);
            }
          }}
        />
      ))}
    </Layout>
  );
}

export const RadioGroup: {
  <V = string>(props: RadioGroupProps<V>): ReactElement;
  displayName?: string;
} = typedMemo(RadioGroupComponent);

RadioGroup.displayName = 'RadioGroup';
